import React from 'react';
import { AxiosError } from 'axios';
import JsonView from '@uiw/react-json-view';

interface RequestResponseViewerProps {
  request: unknown;
  response?: unknown;
  error?: unknown;
}

function getResponseDetails(
  response: unknown,
  error: unknown
): { status: number | string; isError: boolean; body: unknown } {
  if (error) {
    if (error instanceof AxiosError) {
      return {
        status: error.response?.status ?? 'ERR',
        isError: true,
        body: error.response?.data ?? error.message,
      };
    }
    if (error instanceof Error) {
      return { status: 'ERR', isError: true, body: error.message };
    }
    return { status: 'ERR', isError: true, body: String(error) };
  }
  return { status: 200, isError: false, body: response };
}

function toJsonValue(body: unknown): object {
  if (body && typeof body === 'object') return body;
  return { value: body };
}

function JsonPanel({
  title,
  badgeText,
  badgeClass,
  body,
  collapsed,
}: {
  title: string;
  badgeText?: string | number;
  badgeClass?: string;
  body: unknown;
  collapsed: number;
}) {
  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>{title}</strong>
        {badgeText !== undefined && (
          <span className={`badge ${badgeClass ?? 'bg-secondary'}`}>{badgeText}</span>
        )}
      </div>
      <div
        className="card-body bg-light p-2"
        style={{ maxHeight: 480, overflow: 'auto' }}
      >
        <JsonView
          value={toJsonValue(body)}
          collapsed={collapsed}
          displayDataTypes={false}
          enableClipboard
          style={{ background: 'transparent', fontSize: 12 }}
        />
      </div>
    </div>
  );
}

export function RequestResponseViewer({ request, response, error }: RequestResponseViewerProps) {
  const { status, isError, body } = getResponseDetails(response, error);

  return (
    <div className="row g-3 mt-1">
      <div className="col-lg-6">
        <JsonPanel
          title="Request"
          badgeText="POST"
          badgeClass="bg-primary"
          body={request}
          collapsed={3}
        />
      </div>
      <div className="col-lg-6">
        <JsonPanel
          title="Response"
          badgeText={status}
          badgeClass={isError ? 'bg-danger' : 'bg-success'}
          body={body}
          collapsed={2}
        />
      </div>
    </div>
  );
}
